package com.example.mystybag.login.logic

import com.example.mystybag.core.logic.base.NavigatorConfig

// ========== ОБЩ КОНФИГ ЗА ВСИЧКИ СТЪПКИ ==========

// Shared config for all merchant registration steps (Step 1-4)
data class MerchantRegistrationSharedConfig(
    // Company details (Step 1)
    var legalEntityName: String = "",
    var bulstat: String = "",
    var companyPhoneNumber: String = "",
    var companyDialCode: String = "",
    /** Set after successful SMS verification on step 1B; included in create-merchant request. */
    var merchantPhoneVerificationCode: String = "",
    var privacyPolicyAccepted: Boolean = false,
    var registeredCompanyStreet: String = "",
    var registeredCompanyCityId: Long? = null,
    var registeredCompanyCityName: String = "",
    var registeredCompanyCountryId: Int? = null,
    var registeredCompanyCountryName: String = "",
    var companyImageFileName: String? = null,
    
    // Signatory details (Step 2) - list of responsible persons
    var signatories: List<com.example.mystybag.login.model.SignatoryItem> = emptyList(),
    
    // Payment data (Step 3)
    var iban: String = "",
    
    // Login details (Step 4)
    var email: String = "",
    var password: String = "",
    var referralCode: String = ""
) : NavigatorConfig()

// ========== СЪБИРАНЕ НА ДАННИ ЗА ТЪРГОВЕЦА ==========

// Step 1: Add company details (name, country, city, street)
data class NavigatorMerchantRegistrationStep1(
    val sharedConfig: MerchantRegistrationSharedConfig = MerchantRegistrationSharedConfig()
) : NavigatorConfig()

// Step 1B: Add company contact (phone number and privacy policy checkbox)
data class NavigatorMerchantRegistrationStep1B(
    val sharedConfig: MerchantRegistrationSharedConfig
) : NavigatorConfig()

// Step 2: Add registered company address
data class NavigatorMerchantRegistrationStep2(
    val sharedConfig: MerchantRegistrationSharedConfig
) : NavigatorConfig()

// Add/Edit MOL screen (separate screen; when Add is pressed, replace with Step2 and updated signatories)
data class NavigatorAddMol(
    val sharedConfig: MerchantRegistrationSharedConfig,
    val editingIndex: Int? = null
) : NavigatorConfig()

// Step 3: Payment Data (IBAN)
data class NavigatorMerchantRegistrationStep3(
    val sharedConfig: MerchantRegistrationSharedConfig
) : NavigatorConfig()

// ========== СЪБИРАНЕ НА ДАННИ ЗА ЛОГИН ==========

// Step 4: Add login details (email)
data class NavigatorMerchantRegistrationStep4(
    val sharedConfig: MerchantRegistrationSharedConfig
) : NavigatorConfig()

// Step 5: Check your inbox (email verification)
data class NavigatorMerchantRegistrationStep5(
    val merchantId: Long,
    val email: String,
    val password: String,
    val signatories: List<com.example.mystybag.login.model.SignatoryItem>,
    val companyPhoneNumber: String,
    val companyDialCode: String,
    val legalEntityName: String,
    val bulstat: String,
    val privacyPolicyAccepted: Boolean,
    val registeredCompanyStreet: String,
    val registeredCompanyCityId: Long?,
    val registeredCompanyCityName: String,
    val registeredCompanyCountryId: Int?,
    val companyImageFileName: String?,
    val iban: String,
    val referralCode: String = "",
    val phoneVerificationCode: String = ""
) : NavigatorConfig()

// ========== СЪБИРАНЕ НА ДАННИ ЗА MERCHANT_LOCATION ==========

// Step 6 (formerly Step 7): Add merchant location details. When locationId != null = edit existing location.
data class NavigatorMerchantRegistrationStep7(
    val merchantId: Long,
    var storeName: String = "",
    var storeAddress: String = "",
    var cityId: Long? = null,
    var cityName: String = "",
    var countryId: Int? = null,
    var countryName: String = "",
    var storePhoneNumber: String = "",
    var dialCode: String = "",
    var packaging: String = "",
    var additionalPickupInstructions: String = "",
    var surpriseBagContainsBuffetFood: Boolean = false,
    val returnToDashboard: Boolean = false, // Flag to return to dashboard after completion
    val locationId: Long? = null, // When non-null, editing existing location (same multi-screen flow, save calls update)
    val initialLatitude: Double? = null, // For edit mode: pre-fill map in Step8
    val initialLongitude: Double? = null,
    var whatYouCouldGet: String = "", // For edit mode: pre-fill in Step9
    var businessCategoryId: Long? = null,
    val existingCoverPhotoUrl: String? = null,
    val existingMenuPhotoUrls: List<String> = emptyList(),
    /**
     * Persisted on Step7 [onPause] so when the screen is covered (e.g. Add Bag) and the ViewModel is recreated,
     * [MerchantLocationTab] accordion state is restored (e.g. Location stays open). Values = [MerchantLocationTab.name].
     */
    var savedStep7ExpandedTab: String? = null,
    var savedStep7SelectedTab: String? = null
) : NavigatorConfig()

// Step 8: Review merchant location
data class NavigatorMerchantRegistrationStep8(
    val merchantId: Long,
    var storeName: String,
    var storeAddress: String,
    var cityId: Long,
    var cityName: String,
    var countryId: Int?,
    var countryName: String,
    var storePhoneNumber: String,
    var dialCode: String,
    var packaging: String = "",
    var additionalPickupInstructions: String = "",
    var surpriseBagContainsBuffetFood: Boolean = false,
    var latitude: Double? = null,
    var longitude: Double? = null,
    val returnToDashboard: Boolean = false,
    val locationId: Long? = null,
    val initialLatitude: Double? = null,
    val initialLongitude: Double? = null,
    var whatYouCouldGet: String = "",
    var businessCategoryId: Long? = null,
    val existingCoverPhotoUrl: String? = null,
    val existingMenuPhotoUrls: List<String> = emptyList()
) : NavigatorConfig()

// Step 9: Merchant location add what you could get
data class NavigatorMerchantRegistrationStep9(
    val merchantId: Long,
    val storeName: String,
    val storeAddress: String,
    val cityId: Long,
    val cityName: String,
    val countryId: Int?,
    val countryName: String,
    val storePhoneNumber: String,
    val dialCode: String,
    val packaging: String = "",
    val additionalPickupInstructions: String = "",
    val surpriseBagContainsBuffetFood: Boolean = false,
    val latitude: Double,
    val longitude: Double,
    var whatYouCouldGet: String = "",
    val returnToDashboard: Boolean = false,
    val locationId: Long? = null,
    val businessCategoryId: Long? = null, // For edit mode: pre-select in AddDescription
    val existingCoverPhotoUrl: String? = null,
    val existingMenuPhotoUrls: List<String> = emptyList()
) : NavigatorConfig()

// Step 9B: Merchant location add menu photos (up to 10). When locationId != null, existingCoverPhotoUrl and existingMenuPhotoUrls are used for edit.
data class NavigatorMerchantRegistrationStep9B(
    val merchantId: Long,
    val storeName: String,
    val storeAddress: String,
    val cityId: Long,
    val cityName: String,
    val countryId: Int?,
    val countryName: String,
    val storePhoneNumber: String,
    val dialCode: String,
    val packaging: String = "",
    val additionalPickupInstructions: String = "",
    val surpriseBagContainsBuffetFood: Boolean = false,
    val latitude: Double,
    val longitude: Double,
    val whatYouCouldGet: String,
    val businessCategoryId: Long,
    val locationCoverImageFileName: String? = null,
    /** Preset cover chosen on Step 9 (URL from [BusinessCategoryItem.coverImageUrls]); no client upload. */
    val selectedPresetCoverImageUrl: String? = null,
    val returnToDashboard: Boolean = false,
    val locationId: Long? = null,
    val existingCoverPhotoUrl: String? = null,
    val existingMenuPhotoUrls: List<String> = emptyList()
) : NavigatorConfig()

// Simplified Navigator for adding location from Dashboard (only requires merchantId)
data class NavigatorAddMerchantLocationFromDashboard(
    val merchantId: Long,
    val companyPhoneNumber: String? = null,
    val companyDialCode: String? = null,
    val registeredCompanyStreet: String? = null,
    val registeredCompanyCityId: Long? = null,
    val registeredCompanyCityName: String? = null
) : NavigatorConfig()

// Prompt screen to add location (shown when merchant has no locations)
data class NavigatorAddLocationPrompt(
    val merchantId: Long,
    val companyPhoneNumber: String? = null,
    val companyDialCode: String? = null,
    val registeredCompanyStreet: String? = null,
    val registeredCompanyCityId: Long? = null,
    val registeredCompanyCityName: String? = null
) : NavigatorConfig()

// Navigator to return to Dashboard after location creation
object NavigatorReturnToDashboard : NavigatorConfig()

// Navigator to return to Locations tab (e.g. after adding location or skipping AddFirstBagPrompt)
object NavigatorReturnToLocations : NavigatorConfig()

// Prompt screen to add first bag after location creation
data class NavigatorAddFirstBagPrompt(
    val merchantLocationId: Long,
    val businessCategoryId: Long? = null,
    val returnToDashboard: Boolean = false // Flag to return to dashboard after bag creation
) : NavigatorConfig()

// Success screen after merchant registration
object NavigatorMerchantRegistrationSuccess : NavigatorConfig()

// Informational screen shown when merchant registration is pending review on login
object NavigatorMerchantRegistrationUnderReview : NavigatorConfig()
